import qs from 'qs';

import useParkstoryApi, { type ApiResponse, Target } from '../../common/data/parkstory/parkstory';
import type { AbstractReturnType, ApiResponseStatus } from '../../common/domain/api';
import useReservationClass, { type ReservationClass, type ReservationClassProps, type ApiReservationClass } from '../domain/resaClass';

import type { ApiResaClassStats } from '@/common/domain/stats';
import { RequestTypeEnum } from '@/common/domain/toast';
import { logException } from '@/common/utils/logger';

interface QueryParams {
    [key: string]: any;
}

const buildQueryString = (params: QueryParams): string => {
    return qs.stringify(params, { arrayFormat: 'brackets', skipNulls: true });
};

interface ResaClassesReturnType extends AbstractReturnType {
    data: ReservationClass[];
}

interface ResaClassReturnType extends AbstractReturnType {
    data: ReservationClass;
}
interface ResaClassStatsReturnType extends AbstractReturnType {
    data: ApiResaClassStats;
}

const useSubscriptionApi = () => {

    const { get, post, put, remove } = useParkstoryApi();

    /*
     * Retreive basic stats from API
     * params withElectric and date optional in API (date default now, withElectric default false)
     * date : Date .toISOString() i.e 2024-09-30T15:00:00.000Z
     */
    const apiGetResaClassStats = async (resaClassId: string, date: string, withElectric: boolean, packetId?: number | string): Promise<ResaClassStatsReturnType> => {
        try {
            let response;
            if (packetId) {
                response = await get<ApiReservationClass>(Target.Reservation, `reservation-classes/${resaClassId}/count-instance?date=${date}&withElectric=${withElectric}&packetId=${packetId}`);
            } else {
                response = await get<ApiReservationClass>(Target.Reservation, `reservation-classes/${resaClassId}/count-instance?date=${date}&withElectric=${withElectric}`);
            } 
        
            const simplifiedResponse: ResaClassStatsReturnType = {
                data: response.data as ApiResaClassStats,
                status: response.status
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error, { resaClassId });
            throw error;
        }
    };

    const apiGetResaClasses = async (): Promise<ResaClassesReturnType> => {
        try {
            const queryParams = buildQueryString({
                filters: { parkingId: { $notNull: true } },
                sort: 'createdAt:asc',
                pagination: { page: 1, pageSize: 200 },
                populate: {
                    
                    billToClient: {populate: { flatFees: true, powerSupplyFees: true}},
                    config: { populate: { features: true, timing: true, notifications: true, adminOptions: true } },
                },
            });

            const response = await get<ApiReservationClass[]>(Target.Reservation, `reservation-classes?${queryParams}`);

            const convertedData: ReservationClass[] = useReservationClass.apiResaClassesToResaClasses(response.data);

            const status: ApiResponseStatus = {
                code: response.status.code,
                text: response.status.text
            };

            const simplifiedResponse: ResaClassesReturnType = {
                data: convertedData,
                meta: response.meta,
                status: status
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    const apiGetResaClass = async (id: number | string): Promise<ResaClassReturnType> => {
        try {
            const queryParams = buildQueryString({
               
                populate: {
                    billToClient: {populate: { flatFees: true, powerSupplyFees: true}},
                    config: { populate: { features: true, timing: true, notifications: true, adminOptions: true } },
                },
            });

            const response = await get<ApiReservationClass[]>(Target.Reservation, `reservation-classes/${id}?${queryParams}`);
            if (!response.data || response.data.length <= 0) {
                throw new Error('Reservation class not found');
            }
            const item = response.data;
            const convertedData: ReservationClass = useReservationClass.apiResaClassToResaClass(item as ApiReservationClass);
            const simplifiedResponse: ResaClassReturnType = {
                data: convertedData,
                status: response.status
            };
            return simplifiedResponse;
        } catch (error) {
            logException(error, { id });
            throw error;
        }
    };
    const apiGetResaClassByClient = async (id: string | number): Promise<ResaClassReturnType> => {
        try {
            const queryParams = buildQueryString({
                filters: { clientId: { $eq: id } },
                sort: 'createdAt:asc',
                populate: {
                    billToClient: {populate: { flatFees: true, powerSupplyFees: true}},
                    config: { populate: { features: true, timing: true, notifications: true, adminOptions: true } },
                },
            });

            const response = await get<ApiReservationClass[]>(Target.Reservation, `reservation-classes?${queryParams}`);
            let convertedData: ReservationClass | null = null;
            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const item = response.data.pop();
                convertedData = useReservationClass.apiResaClassToResaClass(item as ApiReservationClass);
            }

            const simplifiedResponse: ResaClassReturnType = {
                data: convertedData ?? ({} as ReservationClass),
                status: response.status
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error, { id });
            throw error;
        }
    };

    const apiCreateResaClass = async (pendingResaClass: ReservationClassProps): Promise<ReservationClass> => {
        try {
            const response = await post<ApiReservationClass>(Target.Reservation, 'reservation-classes', pendingResaClass);
                        
            const convertedData: ReservationClass = useReservationClass.apiResaClassToResaClass(response.data);

        
            return convertedData;
        } catch (error) {
            logException(error, { clientId: pendingResaClass.clientId, parkingId: pendingResaClass.parkingId });
            throw error;
        }
    };

    const apiUpdateResaClass = async (resaClass: ReservationClass): Promise<ResaClassReturnType> => {

        try {
            const convertedReqResaClass = useReservationClass.resaClassToPostResaClass(resaClass);
            const resaClassId = resaClass.documentId || resaClass.id;
            const queryParams = buildQueryString({
                
            });
            const response = await put<ApiReservationClass>(Target.Reservation, `reservation-classes/${resaClassId}?${queryParams}`, convertedReqResaClass);
            const convertedData: ReservationClass = useReservationClass.apiResaClassToResaClass(response.data);
        
            const simplifiedResponse: ResaClassReturnType = {
                data: convertedData,
                status: response.status
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error, { resaClassId: resaClass.documentId ?? resaClass.id });
            throw error;
        }
    };

    // Grant access to a group
    const apiGrantAccessToResaClass = async (
        id: number,
        adminGroupRef: string,
    ): Promise<ApiResponse<ReservationClass>> => {
        try {
            const response = await post<ReservationClass>(
                Target.Reservation,
                `reservation-classes/${id}/grant`,
                { groupRef: adminGroupRef, verbs: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'GRANT', 'REVOKE'] },
            );
            return response;
        } catch (error) {
            logException(error, { id });
            throw error;
        }
    };

    // Grant access to a group
    const apiGrantReadAccessToResaClass = async (
        id: number,
        adminGroupRef: string,
    ): Promise<ApiResponse<ReservationClass>> => {
        try {
            const response = await post<ReservationClass>(
                Target.Reservation,
                `reservation-classes/${id}/grant`,
                { groupRef: adminGroupRef, verbs: ['READ'] },
            );
            return response;
        } catch (error) {
            logException(error, { id });
            throw error;
        }
    };

    const apiDeleteResaClass = async (id: number | string): Promise<ApiResponseStatus> => {
        const response = await remove(Target.Reservation, `reservation-classes/${id}`);

        const status: ApiResponseStatus = {
            code: response.status.code,
            requestType: RequestTypeEnum.operationFailed,
            text: response.status.text,

        };
        return status;
    };
    return {
        apiCreateResaClass,
        apiDeleteResaClass,
        apiGetResaClass,
        apiGetResaClassByClient,
        apiGetResaClasses,
        apiGrantAccessToResaClass,
        apiGrantReadAccessToResaClass,
        apiGetResaClassStats,
        apiUpdateResaClass
    };
};

export default useSubscriptionApi;